import useAnalyticsContext from '@hooks/useAnalyticsContext';
import {
	isAllCategoriesPageAtom,
	selectedCategoryCodesAtom,
	selectedCategoryCodesResolutionFailedAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { useAppSelector } from '@redux/hooks';
import type { EventProperties } from '@typings/analytics';
import type { Raw } from '@typings/vendor';
import type { ImpressionDetails } from '@typings/vendorImpressions';
import {
	ALL_CATEGORIES_RESULTS_SOURCE_PAGE,
	CATEGORY_RESULTS_SOURCE_PAGE,
	SEMANTIC_RESULTS_SOURCE_PAGE,
	buildCrossCategoryTrackingProperties,
	resolveVendorSearchSourcePage,
} from '@utils/cross-category-tracking';
import { determineReferredMarketCode } from '@utils/index';
import { isFromSearchPage } from '@utils/regex';
import type { ClassesRecord } from '@xo-union/react-css-modules';
import type { VendorCardClasses } from '@xo-union/tk-component-vendor-card';
import { useAtomValue } from 'jotai';
import React, { useMemo, useCallback, memo, type ReactNodeArray } from 'react';
import AnalyticsConstants from '../../../constants/analytics';
import { VENDOR_REFERRED_MARKET_CODE } from '../../../constants/cookies';
import Vendor from '../../decorators/vendor';
import VendorAnalytics from '../../decorators/vendorAnalytics';
import { cookieGetItem } from '../../utils/cookie/consentManagementCookie';
import Card from '../card';

export interface CardProps {
	isBOW: boolean;
	isFeatured: boolean;
	lazyLoadImage: boolean;
	isNew: boolean;
}

interface TrackableCardProps {
	cardUiLocation: string | null;
	cardProps: CardProps;
	handleSave: (vendor: Raw) => void;
	impressionDetails: ImpressionDetails | null;
	isSaved: boolean;
	showHeart: boolean;
	trackingData: EventProperties;
	type: string;
	vendor: Raw;
	vendorCardClasses: Partial<ClassesRecord<VendorCardClasses>>;
	children: ReactNodeArray;
	newTagEnabled?: boolean;
	aiPhotoEnabled?: boolean;
	renderOdsCard?: boolean;
	centerFavoriteSnackbar?: boolean;
	isDirectoryPageMinPeakPrice?: boolean;
}

export const TrackableCard = (props: TrackableCardProps) => {
	const isReferred = useAppSelector((state) => state.settings.isReferred);
	const marketCode = useAppSelector((state) => state.settings.marketCode);
	const referrer = useAppSelector((state) => state.requestInfo.info.referrer);
	const storefront = useAppSelector((state) => state.vendor.vendor);
	const isAllCategoriesPage = useAtomValue(isAllCategoriesPageAtom);
	const selectedCategoryCodes = useAtomValue(selectedCategoryCodesAtom);
	const selectedCategoriesResolutionFailed = useAtomValue(
		selectedCategoryCodesResolutionFailedAtom,
	);
	const {
		cardUiLocation,
		cardProps,
		handleSave,
		impressionDetails,
		isSaved,
		showHeart,
		trackingData,
		type,
		vendor,
		vendorCardClasses,
		children,
		newTagEnabled = false,
		aiPhotoEnabled = false,
		renderOdsCard = true,
		centerFavoriteSnackbar = false,
		isDirectoryPageMinPeakPrice = false,
	} = props;

	const vendorAnalytics = useMemo(
		() => new VendorAnalytics(new Vendor(vendor)),
		[vendor],
	);
	const { track } = useAnalyticsContext();

	const handleClick = useCallback(() => {
		const cookieMarketCode = cookieGetItem(VENDOR_REFERRED_MARKET_CODE);
		const isFromSearch = isFromSearchPage(referrer);
		const currentSourcePage = trackingData?.properties?.sourcePage;
		const shouldNormalizeSourcePage =
			!currentSourcePage ||
			currentSourcePage === CATEGORY_RESULTS_SOURCE_PAGE ||
			currentSourcePage === ALL_CATEGORIES_RESULTS_SOURCE_PAGE ||
			currentSourcePage === SEMANTIC_RESULTS_SOURCE_PAGE;
		const normalizedSourcePage = shouldNormalizeSourcePage
			? resolveVendorSearchSourcePage({
					isSemanticSearch: currentSourcePage === SEMANTIC_RESULTS_SOURCE_PAGE,
					isAllCategoriesPage,
				})
			: currentSourcePage;
		const trackingProps = {
			...trackingData?.properties,
			sourcePage: normalizedSourcePage,
			vendorReferredMarketCode: determineReferredMarketCode({
				cookieMarketCode,
				isFromSearch,
				isReferred,
				marketCode,
			}),
			...(cardUiLocation === 'vendor network' && {
				sourceContent: 'vendor network',
				sourcePage: storefront?.isPaid
					? 'paid storefront'
					: 'unpaid storefront',
			}),
			...buildCrossCategoryTrackingProperties({
				selectedCategories: selectedCategoryCodes,
				selectedCategoriesResolutionFailed,
			}),
		};

		track({
			event: AnalyticsConstants.CLICK_THROUGH_TO_VENDORS,
			properties: { ...vendorAnalytics.baseEventProps(), ...trackingProps },
		});
	}, [
		cardUiLocation,
		isReferred,
		marketCode,
		storefront,
		trackingData,
		selectedCategoryCodes,
		selectedCategoriesResolutionFailed,
		isAllCategoriesPage,
		referrer,
		vendorAnalytics,
		track,
	]);

	return (
		<Card
			cardUiLocation={cardUiLocation}
			cardProps={cardProps}
			handleSave={handleSave}
			impressionDetails={impressionDetails}
			isSaved={isSaved}
			onClick={handleClick}
			showHeart={showHeart}
			type={type}
			vendor={vendor}
			vendorCardClasses={vendorCardClasses}
			newTagEnabled={newTagEnabled}
			aiPhotoEnabled={aiPhotoEnabled}
			renderOdsCard={renderOdsCard}
			centerFavoriteSnackbar={centerFavoriteSnackbar}
			isDirectoryPageMinPeakPrice={isDirectoryPageMinPeakPrice}
		>
			{children}
		</Card>
	);
};

export default memo(TrackableCard);
